import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Overview from "@/components/project/Overview";
import IndexManager from "@/components/project/IndexManager";
import GeneratePanel from "@/components/project/GeneratePanel";
import DocumentsPanel from "@/components/project/DocumentsPanel";
import ExportPanel from "@/components/project/ExportPanel";
import QualityPanel from "@/components/project/QualityPanel";
import Settings from "@/components/project/Settings";

const countNodes = (nodes) => {
  let n = 0;
  for (const x of nodes) {
    n += 1;
    if (x.children && x.children.length) n += countNodes(x.children);
  }
  return n;
};

export default function Project() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tree, setTree] = useState([]);
  const [status, setStatus] = useState(null);
  const [tab, setTab] = useState("overview");
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const t = searchParams.get("tab");
    const valid = ["overview", "index", "generate", "documents", "quality", "export", "settings"];
    if (t && valid.includes(t)) setTab(t);
  }, [searchParams]);
  const pollRef = useRef(null);
  const treeCountRef = useRef(0);

  const loadProject = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
    } catch {
      navigate("/dashboard");
    }
  }, [id, navigate]);

  const loadTree = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}/nodes`);
      treeCountRef.current = countNodes(data);
      setTree(data);
    } catch {
      // Very large projects can exceed the gateway timeout on the full-tree fetch.
      // The app stays usable from /status counts, so fail quietly instead of blanking tabs.
    }
  }, [id]);

  const loadStatus = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}/status`);
    setStatus(data);
    return data;
  }, [id]);

  useEffect(() => {
    loadProject();
    loadTree();
    loadStatus();
  }, [loadProject, loadTree, loadStatus]);

  // Poll while a job is active
  useEffect(() => {
    const active = status?.active_job;
    const generating = status?.counts?.generating > 0;
    if (active || generating) {
      pollRef.current = setInterval(async () => {
        await loadStatus();
        // For very large books, refetching the whole tree every 3s is wasteful; live counts
        // come from /status. Skip the tree refetch past ~1500 nodes (refreshes on tab actions).
        if (treeCountRef.current <= 1500) await loadTree();
      }, 3000);
    }
    return () => clearInterval(pollRef.current);
  }, [status?.active_job, status?.counts?.generating, loadStatus, loadTree]);

  if (!project) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <span className="animate-pulse font-serif text-xl text-muted-foreground">Loading…</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <TopBar>
        <div className="hidden truncate px-2 font-display text-lg font-medium sm:block">
          {project.title}
        </div>
      </TopBar>
      <main className="mx-auto max-w-6xl px-6 py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-8 h-auto flex-wrap gap-1 rounded-md border border-border bg-card p-1">
            {[
              ["overview", "Console"],
              ["index", "Blueprint"],
              ["generate", "Processor"],
              ["documents", "Vault"],
              ["quality", "Quality"],
              ["export", "Output"],
              ["settings", "Settings"],
            ].map(([v, l]) => (
              <TabsTrigger
                key={v}
                value={v}
                data-testid={`tab-${v}`}
                className="rounded-sm px-4 py-1.5 font-mono text-xs uppercase tracking-wider data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview">
            <Overview project={project} status={status} tree={tree} onGoTo={setTab} />
          </TabsContent>
          <TabsContent value="index">
            <IndexManager
              projectId={id}
              project={project}
              tree={tree}
              reloadTree={loadTree}
              reloadProject={loadProject}
            />
          </TabsContent>
          <TabsContent value="generate">
            <GeneratePanel
              projectId={id}
              project={project}
              tree={tree}
              status={status}
              reloadStatus={loadStatus}
              reloadTree={loadTree}
            />
          </TabsContent>
          <TabsContent value="documents">
            <DocumentsPanel projectId={id} project={project} tree={tree} reloadTree={loadTree} />
          </TabsContent>
          <TabsContent value="export">
            <ExportPanel projectId={id} project={project} tree={tree} />
          </TabsContent>
          <TabsContent value="quality">
            <QualityPanel projectId={id} tree={tree} />
          </TabsContent>
          <TabsContent value="settings">
            <Settings project={project} reloadProject={loadProject} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
